// ============================================================
// Entry point — Even Hub plugin (東京スカウター)
// ============================================================

import { Bridge } from './bridge'
import { createLocationProvider, type LocationProvider } from './geo/location'
import { MockLocationProvider } from './geo/mock-location-provider'
import { VehicleAggregator } from './geo/aggregator'
import { GtfsRtPoller } from './data/gtfs-rt-poller'
import { KantoBusRtPoller } from './data/kanto-bus-rt-poller'
import { TrainStateClient } from './data/train-state-client'
import { TrainGtfsRtPoller } from './data/train-gtfs-rt-poller'
import { fetchStations, fetchBusstops } from './data/odpt-client'
import { GtfsStatic } from './data/gtfs-static'
import { RailwayShapeStore } from './data/railway-shapes'
import { BuildingTileStore } from './data/building-tiles'
import { SonarPage } from './ui/page-sonar'
import { PhoneView } from './ui/phone-view'
import { RADIUS_STEPS, DEFAULT_RADIUS_M, OPERATORS, RAIL_OPERATORS, GTFS_STATIC_URL_TOEI_BUS, GTFS_STATIC_URL_KANTO_BUS, DEFAULT_MOCK_LOCATION } from './config'
import type { Entity, Fix, NearbyState, SonarConfig } from './types'

const GPS_FALLBACK_TIMEOUT_MS = 8000

// ───────── DOM debug helpers (phone WebView 上の確認用) ─────────
const $ = (id: string) => document.getElementById(id)
const setStatus = (id: string, text: string, cls?: 'ok' | 'warn' | 'err') => {
  const el = $(id); if (!el) return
  el.textContent = text
  el.className = `status${cls ? ' ' + cls : ''}`
}
const log = (s: string) => {
  const el = $('debug-log'); if (!el) return
  el.textContent = (el.textContent ?? '') + `\n${new Date().toISOString().slice(11, 19)}  ${s}`
}

// ───────── main ─────────

declare const __APP_VERSION__: string
const BUILD_TIMESTAMP = new Date().toISOString()

async function main() {
  log(`booting Tokyo Scouter v${__APP_VERSION__ ?? '?'} (built around ${BUILD_TIMESTAMP})`)

  // 1) Bridge 接続
  const bridge = new Bridge()
  let raw
  try {
    raw = await bridge.connect()
    setStatus('bridge-status', '✅ Bridge connected', 'ok')
  } catch (err) {
    setStatus('bridge-status', `❌ Bridge: ${(err as Error).message}`, 'err')
    return
  }

  // 2) 位置プロバイダ — real GPS が応答しなければ Mock にフォールバック
  let location: LocationProvider = createLocationProvider()
  await location.start()
  setStatus('location-status', `📍 Location: 取得中…`, 'warn')

  // 3) 集約器・データソース
  const aggregator = new VehicleAggregator()

  // Railway 線分形状 (地図 view 用) — 駅座標との照合のため後で load
  const railwayStore = new RailwayShapeStore()
  railwayStore.setLogger(log)

  // PLATEAU 建物フットプリント (地図 view 用) — 必要なタイルだけ遅延 fetch
  const buildingStore = new BuildingTileStore()
  buildingStore.setLogger(log)
  // 本番では origin は同じ CloudFront ドメインを指すので空文字 (相対 URL) で OK

  // 静的レイヤ (駅・バス停) — auth 必要なので key 無いと取れない
  // 失敗しても致命ではない (バスだけで動作可能) ので個別に try
  // つくば方面: TX (MIR) と関東鉄道、TWR りんかい線 も含めて取得
  const stations = await fetchStations(Array.from(RAIL_OPERATORS))
    .catch((err) => { log(`stations fetch failed: ${(err as Error).message}`); return [] })
  const busstops = await fetchBusstops([OPERATORS.toei, OPERATORS.kantoBus])
    .catch((err) => { log(`busstops fetch failed: ${(err as Error).message}`); return [] })

  const stops: Entity[] = [
    ...stations.map((s) => ({
      id: `station:${s['owl:sameAs']}`,
      kind: 'station' as const,
      lat: s['geo:lat'],
      lon: s['geo:long'],
      moving: false,
      label: s['dc:title'] ?? '駅',
    })),
    ...busstops.map((b) => ({
      id: `busstop:${b['owl:sameAs']}`,
      kind: 'busstop' as const,
      lat: b['geo:lat'],
      lon: b['geo:long'],
      moving: false,
      label: b['dc:title'] ?? 'バス停',
    })),
  ]
  aggregator.setStatic(stops)
  if (stations.length + busstops.length === 0) {
    setStatus('data-status', '⚠️ Static: 0 件 — ODPT_CONSUMER_KEY 未設定なら .env.local に追加', 'warn')
  } else {
    setStatus('data-status', `📡 Static: ${stations.length} 駅 / ${busstops.length} バス停`, 'ok')
  }

  // 駅座標が揃ったら Railway 線分を構築 (stationOrder + 駅座標から polyline)
  const stationCoords = new Map<string, [number, number]>()
  for (const s of stations) {
    stationCoords.set(s['owl:sameAs'], [s['geo:long'], s['geo:lat']])
  }
  railwayStore.setStationCoords(stationCoords)
  railwayStore
    .load(Array.from(RAIL_OPERATORS))
    .then(() => log(`railway shapes loaded: ${railwayStore.count()} lines`))
    .catch((err) => log(`railway shapes load failed: ${(err as Error).message}`))

  // GTFS Static (route_id → 系統名, trip_id → 行き先) を読み込み
  //      Bus label を友好的にするための辞書。失敗しても進める。
  const gtfsStaticToei = new GtfsStatic()
  if (GTFS_STATIC_URL_TOEI_BUS) {
    gtfsStaticToei
      .load(GTFS_STATIC_URL_TOEI_BUS)
      .then(() => log(`gtfs static (Toei) loaded: ${gtfsStaticToei.routeCount()} routes`))
      .catch((err) => log(`gtfs static (Toei) load failed: ${(err as Error).message}`))
  }
  const gtfsStaticKanto = new GtfsStatic()
  if (GTFS_STATIC_URL_KANTO_BUS) {
    gtfsStaticKanto
      .load(GTFS_STATIC_URL_KANTO_BUS)
      .then(() => log(`gtfs static (Kanto) loaded: ${gtfsStaticKanto.routeCount()} routes`))
      .catch((err) => log(`gtfs static (Kanto) load failed: ${(err as Error).message}`))
  }

  // 4) 車両ポーラー
  const busPoller = new GtfsRtPoller()
  busPoller.setGtfsStatic(gtfsStaticToei)
  const kantoBusPoller = new KantoBusRtPoller()
  kantoBusPoller.setGtfsStatic(gtfsStaticKanto)

  // 都営の鉄道は GTFS-RT で本物の位置が取れる (バスと同じ形式)
  const trainGtfsRt = new TrainGtfsRtPoller()
  // それ以外 (Metro / JR / TX / TWR 等) は odpt:Train で区間情報 → 駅位置補間
  const trainClient = new TrainStateClient()

  let latestToeiBuses: Entity[] = []
  let latestKantoBuses: Entity[] = []
  let latestToeiTrains: Entity[] = []
  let latestOtherTrains: Entity[] = []
  const recomputeVehicles = () => {
    aggregator.setVehicles([
      ...latestToeiBuses, ...latestKantoBuses,
      ...latestToeiTrains, ...latestOtherTrains,
    ])
  }
  busPoller.subscribe((vs) => { latestToeiBuses = vs; recomputeVehicles() })
  kantoBusPoller.subscribe((vs) => { latestKantoBuses = vs; recomputeVehicles() })
  trainGtfsRt.subscribe((ts) => { latestToeiTrains = ts; recomputeVehicles() })
  trainClient.subscribe((ts) => { latestOtherTrains = ts; recomputeVehicles() })

  busPoller.start()
  kantoBusPoller.start()
  trainGtfsRt.start()
  try {
    await trainClient.initStations([
      OPERATORS.tokyoMetro, OPERATORS.jrEast, OPERATORS.twr,
      OPERATORS.tx, OPERATORS.kantoRailway,
    ])
    trainClient.start()
  } catch (err) {
    log(`train init failed: ${(err as Error).message}`)
  }

  // 5) ページ (シングルページ設計 — sonar のみ)
  //    map / details への遷移は廃止。運行情報は sonar 左パネルに統合済み。
  const sonarPage = new SonarPage(raw)
  const sonarConfig: SonarConfig = { radiusM: DEFAULT_RADIUS_M }
  void railwayStore  // sonar からは未使用だが将来用に instantiate は維持
  void buildingStore

  // iPhone 側 (WebView) 用の companion view (Leaflet + OSM)
  const phoneView = new PhoneView()
  phoneView.init()

  // ── 初期ページ install ──
  const installInitial = async (fix: Fix) => {
    const state = aggregator.build(fix, { radiusM: sonarConfig.radiusM })
    await sonarPage.install(state)
    setStatus('page-status', '📺 Page: SONAR', 'ok')
  }

  // 6) 描画ループ — data-change driven。データ更新時に即描画、
  //    無変化時も SAFETY_INTERVAL ごとに保険描画。
  let lastRender = 0
  let renderInFlight = false
  let renderScheduled = false  // 「飛んできたが間隔内なので timer 待ち」フラグ
  let transitioning = false    // page install 中 / shutDown 中は render を skip
  let shutdownPendingTimer: number | null = null  // shutDownPageContainer 後の復帰 fallback
  const MIN_RENDER_INTERVAL_MS = 400   // 連続描画の最小間隔
  const SAFETY_INTERVAL_MS = 3000      // 無変化時の保険再描画間隔

  const renderTick = async () => {
    if (renderInFlight || transitioning) { renderScheduled = true; return }
    const fix = location.current()
    if (!fix) return
    renderInFlight = true
    lastRender = Date.now()
    renderScheduled = false
    try {
      const state = aggregator.build(fix, { radiusM: sonarConfig.radiusM })
      // 単一ページ設計: 常に sonar を更新
      await sonarPage.update(state)
      phoneView.update(state)
    } finally {
      renderInFlight = false
      if (renderScheduled && !transitioning) {
        renderScheduled = false
        void renderTick()
      }
    }
  }

  const requestRender = () => {
    const since = Date.now() - lastRender
    if (since < MIN_RENDER_INTERVAL_MS) {
      // 直前に描画した直後なので、gap が空くまで待ってから描画
      if (!renderScheduled) {
        renderScheduled = true
        setTimeout(() => {
          renderScheduled = false
          void renderTick()
        }, MIN_RENDER_INTERVAL_MS - since)
      }
      return
    }
    void renderTick()
  }

  setInterval(() => void renderTick(), SAFETY_INTERVAL_MS)

  // 初期 fix を待ってから install — 8s でタイムアウトしたら Mock にフォールバック
  // NOTE: location.subscribe() は既に fix がある場合 handler を同期的に呼ぶので、
  //       `const off = ...` を closure 内で off() するパターンは TDZ になる。
  //       off を let で先に宣言してから assign する形にすること。
  const firstFix = await new Promise<Fix | null>((resolve) => {
    let done = false
    let off: () => void = () => {}
    const finish = (fix: Fix | null) => {
      if (done) return
      done = true
      off()
      resolve(fix)
    }
    off = location.subscribe((fix) => finish(fix))
    setTimeout(() => finish(null), GPS_FALLBACK_TIMEOUT_MS)
  })

  let activeFix: Fix
  if (firstFix) {
    setStatus('location-status', `📍 GPS  ${firstFix.lat.toFixed(4)}, ${firstFix.lon.toFixed(4)}`, 'ok')
    activeFix = firstFix
  } else {
    log(`GPS が ${GPS_FALLBACK_TIMEOUT_MS}ms 以内に応答しないので Mock (東京駅) にフォールバック`)
    location.stop()
    location = new MockLocationProvider({ start: DEFAULT_MOCK_LOCATION, walk: null })
    await location.start()
    activeFix = location.current()!
    setStatus('location-status', `📍 Mock (東京駅) — GPS タイムアウト`, 'warn')
  }

  // (mock にフォールバックされている可能性があるので) install してから先で subscribe を貼り直す
  await installInitial(activeFix)

  // raw event を in-page log にも流す (どんな構造で来てるか可視化する)
  bridge.onRawEvent((event) => {
    try {
      // 上位 keys
      log(`raw top keys=[${Object.keys(event ?? {}).join(',')}]`)
      // sysEvent / textEvent の中身もダンプ
      if (event?.sysEvent) {
        const s = event.sysEvent
        log(`  sysEvent keys=[${Object.keys(s).join(',')}]`)
        log(`  sysEvent json=${safeJson(s)}`)
      }
      if (event?.textEvent) {
        const t = event.textEvent
        log(`  textEvent keys=[${Object.keys(t).join(',')}]`)
        log(`  textEvent json=${safeJson(t)}`)
      }
      if (event?.jsonData) {
        log(`  jsonData=${safeJson(event.jsonData).slice(0, 200)}`)
      }
    } catch (err) {
      log(`raw event log failed: ${(err as Error).message}`)
    }
  })

  // 7) 入力イベント
  //   click       → ページサイクル (sonar → map → details → sonar)
  //   doubleClick → root (sonar) なら終了確認ダイアログ、それ以外なら root に戻る
  //                 (Even Hub 審査要件: root page double-tap = shutDownPageContainer(1))
  //   scrollTop   → 半径アップ
  //   scrollBottom→ 半径ダウン
  bridge.on(async (e) => {
    const fix = location.current()
    if (!fix) return
    void aggregator.build(fix, { radiusM: sonarConfig.radiusM })  // 参照保持用 (events で利用しないが将来用)

    log(`event: ${e.type}`)
    switch (e.type) {
      case 'click': {
        // 単一ページ設計: tap は noop (画面遷移なし)
        // sonar 内のすべての情報 (spotlight / 最寄駅 / 運行情報) は常に表示済み
        log('click → noop (single-page design)')
        break
      }
      case 'doubleClick': {
        // Hub 審査要件: root page で double-tap = shutDownPageContainer(1) (終了確認)
        log('doubleClick → shutDownPageContainer(1)')
        transitioning = true
        // simulator は mode 1 のダイアログ UI を持たないため fallback timer で sonar を復帰
        shutdownPendingTimer = window.setTimeout(async () => {
          if (!transitioning) return
          log('shutdown fallback: no lifecycle event in 3s → re-installing sonar')
          transitioning = false
          try {
            const fix2 = location.current()
            if (fix2) {
              const state2 = aggregator.build(fix2, { radiusM: sonarConfig.radiusM })
              await sonarPage.install(state2)
            }
          } catch (err) {
            log(`shutdown fallback install failed: ${(err as Error).message}`)
          }
        }, 3000)
        try {
          await raw.shutDownPageContainer(1)
        } catch (err) {
          log(`shutDownPageContainer failed: ${(err as Error).message}`)
          if (shutdownPendingTimer != null) {
            clearTimeout(shutdownPendingTimer)
            shutdownPendingTimer = null
          }
          transitioning = false
        }
        break
      }
      case 'scrollTop': {
        sonarConfig.radiusM = nextRadius(sonarConfig.radiusM, +1)
        requestRender()
        break
      }
      case 'scrollBottom': {
        sonarConfig.radiusM = nextRadius(sonarConfig.radiusM, -1)
        requestRender()
        break
      }
      case 'foregroundEnter': {
        busPoller.start()
        kantoBusPoller.start()
        trainGtfsRt.start()
        trainClient.start()
        location.start()
        // 終了ダイアログがキャンセルされて戻ってきたケースを想定して
        // transitioning を解除し、render を再開する
        transitioning = false
        if (shutdownPendingTimer != null) {
          clearTimeout(shutdownPendingTimer)
          shutdownPendingTimer = null
        }
        requestRender()
        break
      }
      case 'foregroundExit': {
        busPoller.stop()
        kantoBusPoller.stop()
        trainGtfsRt.stop()
        trainClient.stop()
        // bg に行ってる間は描画する意味が無いので transitioning を立てておく
        transitioning = true
        // 実機が foregroundExit を送ってきたら、それで lifecycle 経路が動いているので
        // fallback timer は不要
        if (shutdownPendingTimer != null) {
          clearTimeout(shutdownPendingTimer)
          shutdownPendingTimer = null
        }
        break
      }
      case 'abnormalExit': {
        log('abnormal BLE exit')
        break
      }
    }
  })

  // 8) state 変化で描画要求 — data-change driven
  location.subscribe(() => requestRender())
  busPoller.subscribe(() => requestRender())
  kantoBusPoller.subscribe(() => requestRender())
  trainGtfsRt.subscribe(() => requestRender())
  trainClient.subscribe(() => requestRender())
}

function nextRadius(current: number, dir: 1 | -1): number {
  const i = RADIUS_STEPS.indexOf(current as any)
  const next = i < 0
    ? RADIUS_STEPS[0]
    : RADIUS_STEPS[Math.min(Math.max(0, i + dir), RADIUS_STEPS.length - 1)]
  return next
}

function safeJson(o: any, max = 300): string {
  try {
    const s = JSON.stringify(o, (_k, v) => (v && typeof v === 'object' && !Array.isArray(v))
      ? Object.fromEntries(Object.entries(v).slice(0, 12)) : v)
    return s.length > max ? s.slice(0, max) + '...' : s
  } catch {
    return '[non-serializable]'
  }
}

main().catch((err) => {
  console.error('[main] fatal', err)
  setStatus('bridge-status', `❌ ${(err as Error).message}`, 'err')
})
